import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Smartphone, Copy, Check, Loader2, Radio, Power } from 'lucide-react';
import { getPublicOrigin } from '../lib/publicUrl';

/**
 * Modal affiché à l'enseignant qui veut diffuser sa présentation cross-device.
 * Affiche : code court + QR code pointant vers /present/live/{code}.
 * Props :
 *   - open (bool)
 *   - code (string | null) — code actif si session déjà démarrée
 *   - onStart () — démarre la session (crée le code)
 *   - onClose () — ferme le modal (sans forcément clore la session)
 *   - onStopCast () — ferme la session cross-device
 *   - loading (bool)
 *   - error (string)
 */
export default function CrossDeviceCastModal({ open, code, onStart, onClose, onStopCast, loading, error }) {
  const [copied, setCopied] = useState(false);
  const publicOrigin = getPublicOrigin();
  const liveUrl = code ? `${publicOrigin}/present/live/${code}` : '';

  useEffect(() => {
    if (open && !code && !loading && onStart) onStart();
  }, [open, code, loading, onStart]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* silent */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="cast-modal">
      <div
        className="w-full max-w-lg rounded-3xl border border-red-500/40 bg-gradient-to-br from-[#160a0c] to-[#0a0a0b] p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Radio size={11} className={loading ? 'animate-pulse' : ''} /> Télécommande cross-device
          </div>
          <button onClick={onClose} data-testid="cast-modal-close" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        {loading && !code && (
          <div className="py-16 text-center">
            <Loader2 size={32} className="animate-spin mx-auto text-red-400 mb-4" />
            <div className="text-white/60 text-[14px]">Création de la session…</div>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-[13px] mb-4">{error}</div>
        )}

        {code && (
          <>
            <h2 className="font-serif-editorial text-white text-[26px] sm:text-[30px] leading-tight mb-2">
              Ouvre l&apos;écran de la salle
            </h2>
            <p className="text-white/55 text-[13.5px] leading-relaxed mb-6">
              Sur ton PC connecté au vidéoprojecteur, sur la Smart TV ou sur la tablette de la salle :
            </p>

            {/* QR + code */}
            <div className="rounded-2xl bg-white p-5 flex items-center gap-5">
              <div className="shrink-0">
                <QRCodeSVG value={liveUrl} size={140} level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-widest text-black/50 font-bold font-mono-spec">Code à saisir</div>
                <div className="font-mono-spec text-[52px] font-black text-black leading-none tracking-[0.15em] my-1" data-testid="cast-code">{code}</div>
                <div className="text-black/60 text-[11.5px] leading-tight">
                  Scanne le QR<br />ou va sur <strong>codevoiture.fr</strong>
                </div>
              </div>
            </div>

            {/* URL copyable */}
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-white/5 border border-white/10 flex items-center text-white/70 text-[12px] font-mono-spec truncate">
                {liveUrl}
              </div>
              <button
                onClick={copyUrl}
                data-testid="cast-copy-url"
                className={`h-10 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition ${copied ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 hover:bg-white/15 text-white'}`}
              >
                {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier</>}
              </button>
            </div>

            {/* Étapes */}
            <div className="mt-6 space-y-2.5">
              {[
                'Sur l\u2019écran de la salle : scanne le QR ou tape le code',
                'La salle passe en plein écran',
                'Ton téléphone devient télécommande — touche l\'écran pour pointer',
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[10px] flex items-center justify-center shrink-0 font-bold">{i + 1}</span>
                  <div className="text-white/80 text-[13px] leading-relaxed">{t}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-2">
              <button
                onClick={onClose}
                data-testid="cast-modal-continue"
                className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2"
              >
                <Smartphone size={14} /> C&apos;est prêt, je pilote depuis mon téléphone
              </button>
              <button
                onClick={onStopCast}
                data-testid="cast-modal-stop"
                title="Arrêter la diffusion cross-device"
                className="h-11 px-4 rounded-full border border-white/15 text-white/70 hover:text-red-300 hover:border-red-500/50 text-[12px] font-semibold inline-flex items-center gap-1.5"
              >
                <Power size={13} /> Arrêter
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
